import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { BUSINESS_TOOL_SECTIONS } from 'constants/businessTool';
import { IUnitedSections } from 'constants/businessToolSections';
import { useBreakpoints } from 'hooks';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getAIAssistantReadinessPercent,
  getBusinessPlan,
  getBusinessPlanRequiredFieldsSelector,
  getBusinessPlanType,
  getBusinessToolChapterSelector,
  getSetActiveSection,
  getSetActiveSubSection,
  getSetAIAssistantReadinessPercent,
} from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useBusinessToolAiContext } from 'pages/BusinessToolPage/components/BusinessToolAiProvider';

import { ReactComponent as AIIcon } from 'assets/icons/AIIcon.svg';
import { ReactComponent as AIIconDefault } from 'assets/icons/AIIconDefault.svg';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Button } from 'components/_basic/Button/Button';
import { DoubleCircularProgress } from 'components/_basic/DoubleCircularProgress';
import { TooltipComponent } from 'components/_basic/TooltipComponent';
import { useGetInitialValues } from 'components/BusinessToolForm/utils/form';
import { useModalContext } from 'components/ModalManager';
import { RedGradientCard } from 'components/RedGradientCard';
import { SaveProjectModal } from 'components/SaveProjectModal/SaveProjectModal';

import { calculateFilledPercentage, getColorForProgress } from 'helpers/businessTool';
import { getValidatedMetadata } from 'helpers/metadataParser';

import styles from './AIAssistantAlertCard.module.scss';

interface IProps {
  onCardClose?: VoidFunction;
  isMobileView?: boolean;
}

export const AIAssistantAlertCard: React.FC<IProps> = ({ onCardClose, isMobileView = false }) => {
  const { t, i18n } = useTranslation();

  const { isMobile } = useBreakpoints();
  const { isAuthenticated } = useAuthenticated();
  const { isLoading, generateWholeDocument } = useBusinessToolAiContext();
  const { showModal, hideModal } = useModalContext();
  const { id: businessPlanId = '' } = useParams();

  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const getBusinessToolChapter = useBusinessToolStore(getBusinessToolChapterSelector);
  const setAIAssistantReadinessPercent = useBusinessToolStore(getSetAIAssistantReadinessPercent);
  const getBusinessPlanRequiredFields = useBusinessToolStore(getBusinessPlanRequiredFieldsSelector);
  const updateMutation = useUpdateBusinessPlan();
  const initialValues = useGetInitialValues();

  const setActiveSection = useBusinessToolStore(getSetActiveSection);
  const setActiveSubsection = useBusinessToolStore(getSetActiveSubSection);

  const isHiddenFillOutCard = businessPlan.metadata.isHiddenFillOutCard;

  const currentGlobal = getBusinessToolChapter(BUSINESS_TOOL_SECTIONS.GLOBAL);
  const initialGlobal = initialValues[BUSINESS_TOOL_SECTIONS.GLOBAL];

  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const requiredFields = getBusinessPlanRequiredFields(businessPlanType);
  const AIAssistantReadinessPercent = useBusinessToolStore(getAIAssistantReadinessPercent);
  const oldPercent = useRef(AIAssistantReadinessPercent);

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const isGenerateWholeDocumentDisabled = businessPlan.metadata.isGeneratedWholeDocument && !isStandardOrLightTariff;

  const [isHidden, setIsHidden] = useState(isHiddenFillOutCard);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const updateMetadata = (value: boolean) => {
    const params = {
      id: businessPlanId,
      body: {
        ...businessPlan,
        metadata: JSON.stringify({
          ...getValidatedMetadata(businessPlan.metadata),
          isHiddenFillOutCard: value,
        }),
      },
    };
    updateMutation.mutateAsync(params);
  };

  const generateWholeDocumentHandler = () => {
    if (!isAuthenticated) {
      showModal(SaveProjectModal, {
        onClose: hideModal,
        title: t('pleaseLogin'),
        description: t('loginForAIGeneration'),
      });
    } else {
      if (isGenerateWholeDocumentDisabled) {
        return;
      } else {
        onCardClose?.();
        generateWholeDocument();
      }
    }
  };

  const percent = useMemo(
    () => calculateFilledPercentage(initialGlobal, currentGlobal, requiredFields),
    [currentGlobal, initialGlobal, requiredFields],
  );

  useEffect(() => {
    setAIAssistantReadinessPercent(percent);
    return () => {
      oldPercent.current = percent;
    };
  }, [percent, setAIAssistantReadinessPercent]);

  useEffect(() => {
    if (percent >= 100 && !isHiddenFillOutCard && isAuthenticated) {
      updateMetadata(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => setIsHidden(true), 60000);
    } else if (percent < 100 && isHiddenFillOutCard && isAuthenticated) {
      setIsHidden(false);
      updateMetadata(false);
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [percent]);

  const handleNavigateToOverview = () => {
    if (businessPlanType === 'ONE_PAGE_SHORT') {
      return;
    }
    const goToSection =
      businessPlanType === 'BANK'
        ? 'FOR_BANK_OVERVIEW'
        : businessPlanType === 'LIGHT'
          ? 'FOR_INVESTORS_OVERVIEW'
          : 'ABOUT_THE_COMPANY';
    setActiveSection(goToSection);
    setActiveSubsection('company');
  };

  const handleNavigateToField = (subsection: 'company' | 'project', elementId: string) => {
    const focusAndScroll = (id: string) => {
      setTimeout(() => {
        const input = document.getElementById(id);
        if (input instanceof HTMLElement) {
          input.focus();
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    };

    if (businessPlanType === 'ONE_PAGE_SHORT') {
      focusAndScroll(`onePage.aboutTheCompany.${elementId}`);
      return;
    }

    if (businessPlanType === 'ONE_PAGE') {
      const goToSection = subsection === 'company' ? 'ABOUT_THE_COMPANY' : 'ABOUT_THE_PROJECT';
      setActiveSection(goToSection);
      focusAndScroll(`onePage.aboutTheCompany.${elementId}`);
      return;
    }

    let goToSection: string;
    let elementPrefix: string;

    switch (businessPlanType) {
      case 'BANK':
        goToSection = 'FOR_BANK_OVERVIEW';
        elementPrefix = 'overview.company';
        break;
      case 'LIGHT':
        goToSection = 'FOR_INVESTORS_OVERVIEW';
        elementPrefix = 'overview.company';
        break;
      default:
        goToSection = 'ABOUT_THE_COMPANY';
        elementPrefix = 'overview.company';
    }

    setActiveSection(goToSection as IUnitedSections);
    setActiveSubsection(subsection);
    focusAndScroll(`${elementPrefix}.${elementId}`);
    onCardClose?.();
  };

  if (isHidden && !isMobile) return null;

  const percentString = percent.toFixed(0);
  const disabled = percent < 100;

  return (
    <RedGradientCard className={clsx(styles.cardWrapper, { [styles.hidden]: isMobileView })}>
      <Typography variant="h5">
        {t('aiAssistantAlertCard.title1')}{' '}
        <Typography
          component="span"
          className={clsx(styles.aiTextDecoration, styles.navigateText)}
          onClick={handleNavigateToOverview}
        >
          "{t('aiAssistantAlertCard.title2')}"
        </Typography>{' '}
        {t('aiAssistantAlertCard.title3')} {i18n.language === 'de' ? ' ' : <br />}
        <Typography component="span" className={styles.aiTextDecoration}>
          {t('aiAssistantAlertCard.title4')}
        </Typography>{' '}
        {t('aiAssistantAlertCard.title5')}
      </Typography>

      <Typography variant="body2" className={styles.description}>
        {t('aiAssistantAlertCard.text1')}
      </Typography>
      <Typography variant="body2" className={styles.description}>
        {t('aiAssistantAlertCard.text2')}
      </Typography>

      <Box className={styles.linksWrapper}>
        <Typography onClick={() => handleNavigateToField('company', 'name')} className={styles.clickableText}>
          1. {t('aiAssistantAlertCard.question1')}
        </Typography>
        {businessPlanType !== 'ONE_PAGE_SHORT' && (
          <Typography onClick={() => handleNavigateToField('company', 'address')} className={styles.clickableText}>
            2. {t('aiAssistantAlertCard.question2')}
          </Typography>
        )}

        <Typography
          onClick={() => handleNavigateToField('company', 'briefCompanyDescription')}
          className={styles.clickableText}
        >
          {`${businessPlanType === 'ONE_PAGE_SHORT' ? '2. ' : '3. '}`}
          {t('aiAssistantAlertCard.question3')}
        </Typography>
        <Typography
          onClick={() =>
            handleNavigateToField(businessPlanType === 'LIGHT' ? 'project' : 'company', 'briefProjectDescription')
          }
          className={styles.clickableText}
        >
          {`${businessPlanType === 'ONE_PAGE_SHORT' ? '3. ' : '4. '}`}
          {t('aiAssistantAlertCard.question4')}
        </Typography>
      </Box>

      <Box className={styles.whiteCard}>
        <Box className={styles.percentMessage}>
          <DoubleCircularProgress color={getColorForProgress(percent)} value={percent} size={24} />
          <Typography variant="h6">
            {percentString}% {t('filledOutPercents')}
          </Typography>
        </Box>
        <TooltipComponent title={t('upgradePlanToGetFeature')} isTooltipHidden={!isGenerateWholeDocumentDisabled}>
          <Button
            size={isMobile ? 'medium' : 'small'}
            variant="outlined"
            color="gradient"
            sx={{ gap: isMobile ? 0 : 1 }}
            disabled={disabled || isLoading}
            onClick={generateWholeDocumentHandler}
            fullWidth={isMobile}
          >
            {t('ai.generateWithAI')} {disabled ? <AIIconDefault className={styles.disabledIcon} /> : <AIIcon />}
          </Button>
        </TooltipComponent>
      </Box>
    </RedGradientCard>
  );
};
